'use client';

import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { useMutation } from '@tanstack/react-query';
import { getAxios } from '@/hooks/use-axios';
import { QRCodeSVG } from 'qrcode.react';
import { IAccount } from '@tokers/shared';
import { useEffect, useState } from 'react';

/**
 * Determine the mobile operating system.
 * This function returns one of 'iOS', 'Android', 'Windows Phone', or 'unknown'.
 *
 * @returns {String}
 */
function getMobileOperatingSystem() {
  var userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  if (/android/i.test(userAgent)) {
    return 'Android';
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'iOS';
  }

  return 'Other';
}

const OS = getMobileOperatingSystem();

export function ReeferAFriend({ account }: { account: IAccount | null }) {
  const [href, setHref] = useState<string | null>(null);
  useEffect(() => {
    setHref(location.href);
  }, []);

  const referralStashMutation = useMutation({
    mutationFn: ({ code }: { code: string }) =>
      getAxios().put('/referral', {
        code,
        width: Math.round(Math.min(window.screen.width, window.screen.height)),
        height: Math.round(Math.max(window.screen.width, window.screen.height)),
      }),
  });

  const handleDownload = async () => {
    if (account?.referralCode) {
      await referralStashMutation.mutateAsync({ code: account.referralCode }).catch(err => {
        alert(JSON.stringify(err.response.data, undefined, 2));
        throw err;
      });
      setTimeout(() => {
        if (OS === 'iOS') {
          window.open('https://apps.apple.com/us/app/tokers-ganja-gamified/id6739708837', '_blank');
        } else if (OS === 'Android') {
          window.open('https://play.google.com/store/apps/details?id=com.tokers.app', '_blank');
        }
      });
    }
  };

  return (
    <div className="w-full h-full flex flex-1 flex-col items-center justify-center bg-[url('/images/tokers-wallpaper-background.png')] bg-cover">
      <div className="backdrop-blur-xs w-full h-full flex flex-1 flex-col items-center justify-center gap-10 bg-black/80 p-10 rounded-3xl text-white">
        <Image alt="" width={100} height={100} src="/tokers-app-icon.png" className="rounded-3xl" />
        {account ? (
          <div className="text-2xl text-center">
            You have been invited to <span className="font-black">TOKERS</span> by{' '}
            {account.firstName}
          </div>
        ) : (
          'Referral link is invalid or expired'
        )}
        {account && OS !== 'Other' && (
          <Button onClick={handleDownload} size="lg" className="text-xl h-16 rounded-xl">
            Download <span className="font-black">TOKERS</span>
          </Button>
        )}
        {account && OS === 'Other' && href && (
          <>
            <QRCodeSVG value={href} width={200} height={200} className="border-4 border-white" />
            <div className="text-center">Scan code above with your mobile device</div>
          </>
        )}
      </div>
    </div>
  );
}
